import React from 'react';
import './ReviewEditor.css';

/**
 * The editor for OutCaribbean's own review of a hotel or restaurant.
 *
 * Shared by the Hotels and Restaurants modules. Fields are laid out in the
 * order a reader meets them on the review page, so what is being edited is
 * obvious without having to open the page.
 *
 * The review's date is held in the form as a YYYY-MM-DD string because that is
 * what <input type="date"> speaks; the API stores a unix timestamp. Use
 * `reviewDateToInput` / `reviewDateToUnix` on the way in and out.
 */

export const reviewDateToInput = (unix) => {
  const n = Number(unix || 0);
  if (!n) return '';
  const d = new Date(n * 1000);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (v) => String(v).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export const reviewDateToUnix = (value) => {
  if (!value) return 0;
  const d = new Date(`${value}T12:00:00`);
  if (Number.isNaN(d.getTime())) return 0;
  return Math.floor(d.getTime() / 1000);
};

/**
 * The review body is a list of blocks, exactly like an article: text, image and
 * video entries in the order they are read. It travels as a JSON string so it
 * fits in one column. A value that will not parse is treated as empty rather
 * than throwing, so one malformed row cannot lock the editor.
 */
export const parseBlocks = (val) => {
  if (Array.isArray(val)) return val;
  if (!val) return [];
  try {
    const parsed = JSON.parse(val);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

export const serializeBlocks = (list) => JSON.stringify(parseBlocks(list));

/**
 * Renders blocks to the HTML the public page shows. Kept in step with the
 * article page's own renderer, so a review reads like one.
 */
export const blocksToHtml = (val) => {
  const blocks = parseBlocks(val);
  return blocks.map((block) => {
    if (block.type === 'text') {
      return block.content || '';
    }
    if (block.type === 'image' && block.content?.images?.length) {
      return block.content.images.map((img) => (
        `<figure class="review-figure">`
        + `<img src="${img.url}" alt="${(img.caption || '').replace(/"/g, '&quot;')}" loading="lazy" />`
        + (img.caption ? `<figcaption>${img.caption}</figcaption>` : '')
        + `</figure>`
      )).join('');
    }
    if (block.type === 'video' && block.content?.url) {
      const url = block.content.url;
      // An uploaded file plays inline; anything else is an embed (YouTube, Vimeo)
      const isFile = /\.(mp4|webm|mov|m4v)(\?|$)/i.test(url);
      const player = isFile
        ? `<video src="${url}" controls preload="metadata" playsinline></video>`
        : `<iframe src="${url}" frameborder="0" allowfullscreen title="${(block.content.caption || 'Video').replace(/"/g, '&quot;')}"></iframe>`;
      return `<figure class="review-figure review-figure-video">${player}`
        + (block.content.caption ? `<figcaption>${block.content.caption}</figcaption>` : '')
        + `</figure>`;
    }
    return '';
  }).join('');
};

const parseList = (val) => {
  if (!val) return [];
  if (Array.isArray(val)) return val.map(v => String(v).trim()).filter(Boolean);
  return String(val).split(/\r?\n/).map(v => v.trim()).filter(Boolean);
};

const hasText = (html) => !!(html && String(html).replace(/<[^>]*>/g, '').trim());

/**
 * A small add/remove list, the same shape the amenities picker uses, so the
 * bullet lists feel like the rest of the form rather than a raw textarea.
 */
function BulletList({ label, hint, placeholder, value, onChange }) {
  const items = parseList(value);
  const [draft, setDraft] = React.useState('');

  const add = () => {
    const v = draft.trim();
    if (!v || items.includes(v)) { setDraft(''); return; }
    onChange([...items, v].join('\n'));
    setDraft('');
  };

  const remove = (item) => onChange(items.filter(i => i !== item).join('\n'));

  return (
    <div className="DashboardFormGroup">
      <label className="DashboardFormLabel">{label}</label>
      {hint && <p className="ReviewHint">{hint}</p>}

      <div className="ReviewBulletRow">
        <input
          type="text"
          className="DashboardFormInput"
          value={draft}
          placeholder={placeholder}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); add(); }
          }}
        />
        <button type="button" className="DashboardButtonSecondary" onClick={add}>Add</button>
      </div>

      {items.length > 0 && (
        <ul className="ReviewBullets">
          {items.map((item) => (
            <li key={item}>
              <span>{item}</span>
              <button type="button" onClick={() => remove(item)} aria-label={`Remove ${item}`}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ReviewEditor({ kind, formData, setFormData, galleryImages = [], mainImage = '', previewUrl = '', onOpenBodyEditor }) {
  const set = (patch) => setFormData(prev => ({ ...prev, ...patch }));

  const isLive = !!formData.review_active;
  const headline = (formData.review_title || '').trim();
  const standfirst = (formData.review_standfirst || '').trim();
  // Blocks are the source of truth; review_content is the HTML rendered from them
  const bodyWritten = hasText(formData.review_content) || parseBlocks(formData.review_blocks).length > 0;

  // What still has to happen before the review is worth publishing. The body
  // is optional: a headline and an opening line make a short review.
  const missing = [];
  if (!headline) missing.push('a headline');
  if (!bodyWritten && !standfirst) missing.push('an opening line or a review');

  // A short account of what is in the body, so its state is visible without
  // having to open the editor.
  const blocks = parseBlocks(formData.review_blocks);
  const counts = blocks.reduce((acc, b) => {
    if (b.type === 'image') acc.images += (b.content?.images?.length || 0);
    else if (b.type === 'video') acc.videos += (b.content?.url ? 1 : 0);
    else if (String(b.content || '').replace(/<[^>]*>/g, '').trim()) acc.text += 1;
    return acc;
  }, { text: 0, images: 0, videos: 0 });

  const blockSummary = bodyWritten
    ? [
        counts.text ? `${counts.text} text block${counts.text === 1 ? '' : 's'}` : null,
        counts.images ? `${counts.images} photo${counts.images === 1 ? '' : 's'}` : null,
        counts.videos ? `${counts.videos} video${counts.videos === 1 ? '' : 's'}` : null,
      ].filter(Boolean).join(' · ') || 'Written'
    : 'Nothing written yet';

  const covers = [mainImage, ...galleryImages].filter(Boolean);
  const uniqueCovers = covers.filter((url, i) => covers.indexOf(url) === i);
  const chosenCover = formData.review_image || '';

  return (
    <section className="ReviewPanel">
      <header className="ReviewPanelHead">
        <div>
          <h3 className="ReviewPanelTitle">Our Review</h3>
          <p className="ReviewPanelSub">
            The review OutCaribbean writes for a {kind} that has paid for one. It gets
            its own page, linked from a button on the {kind}'s page.
          </p>
        </div>

        <div className="ReviewStatus">
          <span className={`ReviewPill ${isLive ? 'is-live' : 'is-draft'}`}>
            {isLive ? 'Live on the site' : 'Draft — nobody can see it'}
          </span>
          <label className="ReviewSwitch">
            <input
              type="checkbox"
              checked={isLive}
              onChange={(e) => set({ review_active: e.target.checked ? 1 : 0 })}
            />
            <span className="ReviewSwitchTrack" aria-hidden="true"><span className="ReviewSwitchKnob" /></span>
            <span className="ReviewSwitchLabel">Publish</span>
          </label>
        </div>
      </header>

      {isLive && missing.length > 0 && (
        <p className="ReviewWarning">
          This review is set to publish but still needs {missing.join(' and ')}. Until
          then the button stays hidden on the {kind}'s page.
        </p>
      )}

      {isLive && missing.length === 0 && previewUrl && (
        <p className="ReviewLive">
          Live at <a href={previewUrl} target="_blank" rel="noreferrer">{previewUrl}</a>
        </p>
      )}

      <ol className="ReviewSteps">
        <li className="ReviewStep">
          <div className="ReviewStepNo">1</div>
          <div className="ReviewStepBody">
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Headline</label>
              <p className="ReviewHint">The first thing a reader sees. Give a verdict, not a name.</p>
              <input
                type="text"
                className="DashboardFormInput"
                maxLength={255}
                value={formData.review_title || ''}
                onChange={(e) => set({ review_title: e.target.value })}
                placeholder="e.g. A grand old lady that still knows how to throw a party"
              />
              <span className="ReviewCount">{headline.length}/255</span>
            </div>

            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Opening line</label>
              <p className="ReviewHint">
                One or two sentences. Sits under the headline, and is the teaser shown
                on the {kind}'s own page.
              </p>
              <textarea
                className="DashboardFormInput"
                rows={3}
                maxLength={500}
                value={formData.review_standfirst || ''}
                onChange={(e) => set({ review_standfirst: e.target.value })}
                placeholder="Why this place is worth a reader's time, in a breath."
              />
              <span className="ReviewCount">{standfirst.length}/500</span>
            </div>
          </div>
        </li>

        <li className="ReviewStep">
          <div className="ReviewStepNo">2</div>
          <div className="ReviewStepBody">
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">The review</label>
              <p className="ReviewHint">
                Written in the same editor as an article: text, photos and video as
                blocks you can drag into order, each with its own caption.
              </p>

              <div className="ReviewBodySummary">
                <div className="ReviewBodyCounts">
                  {blockSummary}
                </div>
                <button
                  type="button"
                  className="DashboardButton"
                  onClick={() => onOpenBodyEditor()}
                >
                  {bodyWritten ? 'Edit the review' : 'Write the review'}
                </button>
              </div>
            </div>
          </div>
        </li>

        <li className="ReviewStep">
          <div className="ReviewStepNo">3</div>
          <div className="ReviewStepBody">
            <p className="ReviewStepLead">The quick-read box beside the review. All optional.</p>

            <div className="ReviewGrid">
              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Reviewed by</label>
                <p className="ReviewHint">Shown as the byline.</p>
                <input
                  type="text"
                  className="DashboardFormInput"
                  value={formData.review_author || ''}
                  onChange={(e) => set({ review_author: e.target.value })}
                  placeholder="e.g. OutCaribbean"
                />
              </div>

              <div className="DashboardFormGroup">
                <label className="DashboardFormLabel">Date reviewed</label>
                <p className="ReviewHint">Leave blank to show no date.</p>
                <input
                  type="date"
                  className="DashboardFormInput"
                  value={formData.review_date || ''}
                  onChange={(e) => set({ review_date: e.target.value })}
                />
              </div>
            </div>

            <BulletList
              label="What we love"
              hint="Short points. Press Enter or Add after each one."
              placeholder="e.g. The rooftop pool at sunset"
              value={formData.review_highlights}
              onChange={(v) => set({ review_highlights: v })}
            />

            <BulletList
              label="Good to know"
              hint="Honest caveats. These build trust, so don't skip them."
              placeholder="e.g. No lift to the upper floors"
              value={formData.review_notes}
              onChange={(v) => set({ review_notes: v })}
            />
          </div>
        </li>

        <li className="ReviewStep">
          <div className="ReviewStepNo">4</div>
          <div className="ReviewStepBody">
            <div className="DashboardFormGroup">
              <label className="DashboardFormLabel">Cover photo</label>
              <p className="ReviewHint">
                {uniqueCovers.length > 0
                  ? `Pick one of this ${kind}'s photos for the top of the review page.`
                  : `Add photos to this ${kind} first, then pick one here.`}
              </p>

              {uniqueCovers.length > 0 && (
                <div className="ReviewCovers">
                  <button
                    type="button"
                    className={`ReviewCover ReviewCoverNone ${!chosenCover ? 'is-chosen' : ''}`}
                    onClick={() => set({ review_image: '' })}
                  >
                    Use the main photo
                  </button>

                  {uniqueCovers.map((url) => (
                    <button
                      key={url}
                      type="button"
                      className={`ReviewCover ${chosenCover === url ? 'is-chosen' : ''}`}
                      style={{ backgroundImage: `url('${url}')` }}
                      onClick={() => set({ review_image: url })}
                      aria-label="Use this photo as the review cover"
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </li>
      </ol>
    </section>
  );
}
